import { useState } from "react";
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { SectionTitle } from "@/components/SectionTitle";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    
    try {
      const response = await fetch('https://formspree.io/f/xzzgnznw', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        toast({
          title: "🎉 Thank you!",
          description: "Your message has been successfully sent. We'll get back to you shortly.",
          duration: 4000, // 4 seconds auto-dismiss
          className: "bg-green-50 border-green-200 text-green-800",
        });
        // Reset form
        (e.target as HTMLFormElement).reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong, please try again.",
        variant: "destructive",
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <PageTitle title="Contact – Asrani Dental – Best Dentist | Best Root Canal | Aligners | Invisalign | Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Get in Touch
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-dental-muted">
              We're here to answer your questions and help you schedule your next visit. Reach out to our friendly team today.
            </p>
          </div>
        </div>
      </section>
      
      {/* Contact Info & Map Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <SectionTitle 
                title="Contact Information" 
                subtitle="Reach Out to Us"
                description="Have questions or ready to schedule your appointment? Use the information below to get in touch with our team."
                alignment="left"
              />
              
              <div className="mt-8 space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mr-4">
                    <Phone size={20} className="text-dental-highlight" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1">Phone</h3>
                    <p className="text-dental-muted mb-1">Call us directly at:</p>
                    <a onClick={(e) => { e.preventDefault(); (window as any).gtag_report_conversion && (window as any).gtag_report_conversion("tel:+918010224989"); }} href="tel:+918010224989" className="text-dental-highlight hover:underline">+91 8010224989</a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mr-4">
                    <Mail size={20} className="text-dental-highlight" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1">Email</h3>
                    <p className="text-dental-muted mb-1">Email us at:</p>
                    <a href="mailto:asranisbdental@gmail.com" className="text-dental-highlight hover:underline">asranisbdental@gmail.com</a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mr-4">
                    <MapPin size={20} className="text-dental-highlight" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1">Address</h3>
                    <p className="text-dental-muted mb-1">Visit our clinic at:</p>
                    <address className="not-italic text-dental-text">
                      2nd Floor, Guman Building<br />
                      Sadar, Nagpur, Maharashtra 440001
                    </address>
                  </div>
                </div>
              </div>
              
              <div className="mt-12">
                <h3 className="text-xl font-serif font-medium mb-4">Office Hours</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-dental-muted">Monday - Friday:</span>
                    <div className="text-right">
                      <div>10:00 AM - 3:00 PM</div>
                      <div>6:00 PM - 9:00 PM</div>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-dental-muted">Saturday:</span>
                    <div className="text-right">
                      <div>10:00 AM - 3:00 PM</div>
                      <div>6:00 PM - 9:00 PM</div>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-dental-muted">Sunday:</span>
                    <div className="text-right">
                      <div>11:00 AM - 2:00 PM</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-12">
                <Button className="btn-primary flex items-center text-base group" onClick={() => window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank")}>
                  Book Appointment
                  <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
            
            <div>
              <div className="bg-white p-4 rounded-xl shadow-soft">
                <div className="aspect-square rounded-lg overflow-hidden">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3721.170271266254!2d79.08215611473774!3d21.14716078594136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c0bfaaaaaaab%3A0x24cdd9b757bc4f0c!2sAsrani%20Dental%20Clinic!5e0!3m2!1sen!2sin!4v1722178057284!5m2!1sen!2sin!4v1722178057284!5m2!1sen!2sin" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen={true} 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Asrani Dental Clinic Location"
                  />
                </div>
                <div className="mt-4">
                  <h3 className="font-medium text-dental-text mb-1">Asrani Dental Clinic</h3>
                  <p className="text-sm text-dental-muted">2nd Floor, Guman Building, Sadar, Nagpur</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Form Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Send Us a Message" 
            subtitle="Get in Touch"
            description="Fill out the form below and our team will get back to you as soon as possible."
            alignment="center"
          />
          
          <div className="max-w-2xl mx-auto mt-12">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-dental-text mb-1">Full Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    required 
                    className="w-full px-4 py-3 rounded-md border border-dental-light/50 focus:border-dental-highlight focus:ring focus:ring-dental-highlight/20 focus:outline-none transition-all" 
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-dental-text mb-1">Email Address</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    className="w-full px-4 py-3 rounded-md border border-dental-light/50 focus:border-dental-highlight focus:ring focus:ring-dental-highlight/20 focus:outline-none transition-all" 
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-dental-text mb-1">Phone Number</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  className="w-full px-4 py-3 rounded-md border border-dental-light/50 focus:border-dental-highlight focus:ring focus:ring-dental-highlight/20 focus:outline-none transition-all" 
                />
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-dental-text mb-1">Subject</label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  className="w-full px-4 py-3 rounded-md border border-dental-light/50 focus:border-dental-highlight focus:ring focus:ring-dental-highlight/20 focus:outline-none transition-all" 
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-dental-text mb-1">Message</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows={5} 
                  required 
                  className="w-full px-4 py-3 rounded-md border border-dental-light/50 focus:border-dental-highlight focus:ring focus:ring-dental-highlight/20 focus:outline-none transition-all resize-none"
                ></textarea>
              </div>
              
              <div>
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Contact;
